import { useCallback, useEffect } from 'react';
import getConfig from 'next/config';

import { logActivity } from 'services/logActivity';
import {
  addTitleAndLocation,
  pushSubscriptionNotification,
  registerServiceWorker,
  subscribeToPushNotifications,
} from 'services/pushNotificationsService';
import { updateNotifications } from 'services/userProfileService';
import { isChrome, isFirefox, isIE } from 'utils';
import { USER_ACTIVITY_EVENTS } from 'utils/constant';

import { useAppSelector } from './useRedux';
const {
  publicRuntimeConfig: { PUSH_NOTIFICATIONS_VAPID_KEY },
} = getConfig();

export const useNativeNotificationPrompt = () => {
  const userId = useAppSelector(({ user }) => user?.userId);
  const criteria = useAppSelector(({ locals }) => locals.criteria);

  const googleOneTapContainer =
    typeof window !== 'undefined'
      ? !!window?.document?.getElementById('googleOneTapContainer')?.innerHTML
      : '';

  const askNotificationPermission = useCallback(() => {
    // 1. userId should not be undefined
    // 2. Check if Google SSO Prompt is visible. If not then detect and ask notification permission
    // 3. Don't ask permission in iPad/iPhone devices due to security reasons
    // 4. Currently push notifications are only supported on Firefox, Chrome and IE browsers.
    // 5. Current version of browser should support push notifications.
    // 6. The notification permission for the site should be set to Ask(default).
    if (
      !(
        userId &&
        !googleOneTapContainer &&
        !navigator.userAgent.match(/ipad|iphone/i) &&
        (isFirefox() || isChrome() || isIE()) &&
        typeof window.Notification !== 'undefined'
      )
    )
      return;
    logActivity({
      activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.BROWSER_NOTIFICATIONS_PROMPT,
    });

    window.Notification &&
      window.Notification.requestPermission()
        .then((permission) => {
          if (permission === 'granted') {
            logActivity({
              activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.BROWSER_NOTIFICATIONS_ACTION,
              optin: true,
            });

            // Register Service worker to get browser's push notifications
            registerServiceWorker()
              .then(() => {
                subscribeToPushNotifications(PUSH_NOTIFICATIONS_VAPID_KEY)
                  .then((subscription) => {
                    if (subscription) {
                      // Save This subscription to the PUSH Server
                      pushSubscriptionNotification(userId, 'subscribe', subscription, false);
                      // Opt-in for push notification channel
                      updateNotifications({
                        pushChannel: { optout: false },
                      });
                      addTitleAndLocation(criteria);
                    }
                  })
                  .catch((error) => {
                    console.error(error);
                  });
              })
              .catch((error) => {
                console.error('Error occurred while registering service worker', error);
              });
          } else {
            logActivity({
              activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.BROWSER_NOTIFICATIONS_ACTION,
              optin: false,
            });
          }
        })
        .catch((e) => console.error(e));
  }, [criteria, userId, googleOneTapContainer]);

  useEffect(() => {
    if (window.Notification && window.Notification.permission === 'default')
      askNotificationPermission();
  }, [askNotificationPermission]);

  return {
    askNotificationPermission,
  };
};
